import { useCallback, useEffect, useRef, useState } from 'react';

// useLocalStorageState — `useState` synced to localStorage.
//
// Reads on first render (lazy initializer). When `key` changes, re-reads
// from the new key — without that, switching contexts (e.g. Stats sub-tab)
// silently overwrites the new key's storage with whatever value the hook
// held for the previous key.
//
// Storage failures are swallowed (private mode, quota, etc.) — same
// behaviour as the legacy app-shell which used direct
// `localStorage.getItem/setItem` without try/catch and only worked
// because the surfaces ran in normal browser sessions.
//
// Generic over T (extending string). Pass `isValid` for narrow string
// unions (e.g. `Lang`/`Route`/`SubTab`) so a stale or hand-edited
// localStorage value falls back instead of corrupting state.

function readLS<T extends string>(
  key: string,
  fallback: T,
  isValid?: (s: string) => s is T,
): T {
  try {
    const stored = window.localStorage.getItem(key);
    if (stored === null) return fallback;
    if (isValid && !isValid(stored)) return fallback;
    return stored as T;
  } catch {
    return fallback;
  }
}

export function useLocalStorageState<T extends string>(
  key: string,
  fallback: T,
  isValid?: (s: string) => s is T,
): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => readLS(key, fallback, isValid));

  // Re-read on key change. Skip on first render (the lazy initializer
  // above already handled it). Without this, every consumer that holds
  // a sub-tab/role-scoped key would read once on mount and then
  // silently keep the wrong value when the key flipped — and the
  // setter below would then overwrite the new key's stored value with
  // the stale state from the old key.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setValue(readLS(key, fallback, isValid));
  }, [key, fallback, isValid]);

  // Setter writes to storage synchronously. Pairing the read effect
  // with a separate write `useEffect([key, value])` would race on key
  // change (the write fires with the *previous* key's value before the
  // read's setValue lands), so we drop the effect and write inside the
  // setter instead.
  const setValueAndWrite = useCallback(
    (next: T) => {
      setValue(next);
      try {
        window.localStorage.setItem(key, next);
      } catch {
        /* ignore quota / private-mode errors */
      }
    },
    [key],
  );

  return [value, setValueAndWrite];
}
